/* eslint-disable react/no-unused-prop-types */
import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Checkbox,
  Button,
  Dropdown,
  Icon,
} from 'semantic-ui-react';
import clone from 'clone';
import { withTranslation } from 'react-i18next';
import { required, numericRange } from '@mhub/web-validations';
import { v4 as uuidv4 } from 'uuid';

import config from '../../config';
import { fetchM } from '../actions/fetchM';
import { flashErrorMessage } from '../actions/flash';
import { TRANSLATION_FILES } from '../constants/translation';
import { isPricingPackageLocked, vnPackageLockMessage } from '../utils/vietnamPricing';
import AppForm from './AppForm';

class CustomiseFormulaModal extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    auth: PropTypes.shape({
      isSuperAdmin: PropTypes.bool,
      isAdminBasic: PropTypes.bool,
    }),
    bookingData: PropTypes.shape(),
    isMobile: PropTypes.bool,
    loading: PropTypes.bool,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    packagePricings: PropTypes.arrayOf(PropTypes.shape()),
    serviceCompanyID: PropTypes.string,
    globalSettings: PropTypes.shape(),
    t: PropTypes.func,
  }
  static getPropsToState = (props) => {
    const { bookingData, open } = props;
    let calculations = [];
    // control modal state externally instead of based on trigger if modalState provided
    if (open && bookingData && bookingData.packagePricing &&
      bookingData.packagePricing.calculations &&
      bookingData.packagePricing.calculations.length > 0) {
      calculations = clone(bookingData.packagePricing.calculations);
    }
    return {
      calculations,
    };
  }
  constructor(props) {
    super(props);
    const state = CustomiseFormulaModal.getPropsToState(props);
    this.validation = {};
    state.calculations.forEach((c) => {
      if (c.isAdhoc) {
        this.validation[c.id] = createRef();
      }
    });

    this.state = {
      ...state,
      loading: false,
      adhocFormulas: [],
      errMessages: [],
    };
  }

  componentDidMount() {
    if (this.props.open) {
      this.fetchPPAdhocFormulas();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.open === true && this.props.open !== nextProps.open) {
      this.fetchPPAdhocFormulas();
    }

    const state = CustomiseFormulaModal.getPropsToState(nextProps);
    this.validation = {};
    state.calculations.forEach((c) => {
      if (c.isAdhoc) {
        this.validation[c.id] = createRef();
      }
    });
    this.setState({ ...state });
  }

  setLoading = (loading = false) => (new Promise((res) => {
    this.setState({ loading }, res);
  }))

  setAdhocFormulas = data => (new Promise((res) => {
    this.setState({ adhocFormulas: data || [] }, res);
  }))

  setComponentState = (state = {}) => (new Promise((res) => {
    this.setState(state, () => {
      res(state);
    });
  }))

  getDevPPUrl = () => {
    const { bookingData, serviceCompanyID } = this.props;
    if (bookingData && bookingData.packagePricing &&
      bookingData.packagePricing.projectPackagePricingID && serviceCompanyID) {
      return `${config.get('developerURL')}/c/${serviceCompanyID}/projects/${bookingData.projectID}/package_pricings/${bookingData.packagePricing.projectPackagePricingID}`;
    }
    return '';
  }

  getFormulaTitle = (title) => {
    switch (title) {
      case 'discount':
        return 'Discount';
      case 'rebate':
        return 'Rebate';
      case 'add_on':
        return 'Add-on';
      case 'free_item':
        return 'Free item';
      case 'custom':
        return 'Custom';
      case 'tax':
        return 'Tax';
      default:
        return '';
    }
  }

  getCalculations = (category) => {
    const { t } = this.props;
    const { calculations } = this.state;
    const cItems = calculations.filter(c => c.category === category && !c.isAdhoc).map(calc => ({
      id: calc.id,
      name: calc.isSubTotal ? t('Sub total') : calc.name,
      formulaTitle: calc.isSubTotal ? 'Subtotal' : this.getFormulaTitle(calc.title),
      checked: calc.isEnabled,
      orderPriority: calc.orderPriority,
      disabled: calc.isRequired,
    }));
    return cItems;
  }

  getAdhocCalculations = (category) => {
    const { calculations } = this.state;
    const cItems = calculations.filter(c => c.category === category && c.isAdhoc).map(calc => ({
      id: calc.id,
      name: calc.name,
      title: calc.title,
      formulaTitle: `${calc.isAdhoc ? 'Adhoc ' : ''}${this.getFormulaTitle(calc.title)}`,
      orderPriority: calc.orderPriority,
      formulaValue: calc.formulaValue,
      formulaValueMin: calc.formulaValueMin,
      formulaValueMax: calc.formulaValueMax,
    }));
    return cItems;
  }

  fetchPPAdhocFormulas = async (calcs) => {
    await this.setLoading(true);
    const { store } = this.context;
    const { t, bookingData } = this.props;
    let res = '';
    try {
      res = await store.dispatch(fetchM(`/projects/${bookingData.projectID}/package_pricings/${bookingData.packagePricing.projectPackagePricingID}/formulas/?isAdhoc=true&isEnabled=true`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(calcs),
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      Bugsnag.notifyException(err);
      store.dispatch(flashErrorMessage({ message: `${t('There was an error with your request. Please try again', { ns: TRANSLATION_FILES.translation })}.` }));
      return;
    }
    const data = await res.json();
    await this.setAdhocFormulas(data);
    await this.setLoading(false);
  }

  handleOnCheck = (e, target) => {
    const { value, checked } = target;
    const { calculations } = this.state;
    const updatedCalculationItems = [...calculations];
    if (updatedCalculationItems.length > 0) {
      const idx = updatedCalculationItems.findIndex(ci => ci.id === value);
      if (idx !== -1) {
        updatedCalculationItems[idx].isEnabled = checked;
      }
      this.setState({ calculations: updatedCalculationItems });
    }
  }

  handleAdhocChange = (event, data) => {
    const { calculations } = this.state;
    const updatedCalculationItems = [...calculations];
    if (updatedCalculationItems.length > 0) {
      const idx = updatedCalculationItems.findIndex(ci => ci.id === data.name);
      if (idx !== -1) {
        updatedCalculationItems[idx].formulaValue = data.value;
      }
      this.setState({ calculations: updatedCalculationItems });
    }
  }

  handleAddAdhoc = (event, data) => {
    const { calculations, adhocFormulas } = this.state;
    const updatedCalculationItems = [...calculations];
    if (adhocFormulas.length > 0) {
      const adhocItem = adhocFormulas.find(a => a.id === data.value);
      if (adhocItem) {
        const id = `temp_${uuidv4()}`;
        const category = adhocItem.calculationType === 'deduct_from_list_price' ? 'list_price' : 'spa_price';
        const orderPriority = calculations.filter(c => c.category === category).length + 1;
        updatedCalculationItems.push({
          id,
          category,
          orderPriority,
          projectPackagePricingItemID: adhocItem.id,
          projectPackagePricingItem: adhocItem,
          name: adhocItem.name,
          description: adhocItem.description,
          code: adhocItem.code,
          title: adhocItem.title,
          format: adhocItem.format,
          calculationType: adhocItem.calculationType,
          rounding: adhocItem.rounding,
          formulaValue: adhocItem.title === 'free_item' ? '' : 0,
          formulaValueMin: adhocItem.formulaValueMin,
          formulaValueMax: adhocItem.formulaValueMax,
          isRequired: false,
          isAdhoc: true,
          isEnabled: true,
        });
        this.validation[id] = createRef();
      }
      this.setState({ calculations: updatedCalculationItems });
    }
  }

  handleRemoveAdhoc = (adhocID) => {
    const { calculations } = this.state;
    const updatedCalculationItems = [...calculations];
    if (updatedCalculationItems.length > 0) {
      const idx = updatedCalculationItems.findIndex(ci => ci.id === adhocID);
      if (idx !== -1) {
        delete this.validation[updatedCalculationItems[idx].id];
        updatedCalculationItems.splice(idx, 1);
      }
      this.setState({ calculations: updatedCalculationItems });
    }
  }

  handleSubmit = async () => {
    const { calculations } = this.state;
    const { onSubmit, onClose, t } = this.props;
    if (onSubmit) {
      const data = {};
      calculations.forEach((c) => {
        if (this.validation[c.id]) {
          data[c.id] = c.formulaValue;
        }
      });
      const validations = await AppForm.validate(this.validation, data);
      const errMessages = validations.filter(v => !v.isValid);
      await this.setComponentState({
        errMessages,
      });
      if (errMessages.length > 0) {
        const { store } = this.context;
        await this.setLoading(false);
        store.dispatch(flashErrorMessage({ header: t('Please fix form errors', { ns: TRANSLATION_FILES.translation }), list: errMessages.map(l => l.message) }));
        return;
      }
      onSubmit(calculations);
    }
    onClose();
  };

  renderFieldHeader = text => (
    <section className="checkboxContainer containerHeader">
      <p className="text-title">
        {text}
      </p>
    </section>
  )

  renderFields = items => items && items.map(item => (
    <section
      className="checkboxContainer"
      key={item.id}
    >
      <Checkbox
        value={item.id}
        checked={item.checked}
        onChange={this.handleOnCheck}
        disabled={item.disabled}
      />
      <div className="checkboxLabel">
        <p className="text-title">{item.name}</p>
        <p className="text-subtitle">{item.formulaTitle}</p>
      </div>
    </section>
  ))

  renderAdhocFields = items => items && items.map((item) => {
    const isFreeItem = item.title === 'free_item';
    const validations = [required];
    if (!isFreeItem) {
      validations.push({
        validate: numericRange,
        option: {
          min: parseInt(item.formulaValueMin, 10),
          max: parseInt(item.formulaValueMax, 10),
        },
      });
    }
    return (
      <section
        className="checkboxContainer"
        key={item.id}
      >
        <AppForm.Input
          style={{ width: '100%' }}
          autoComplete="off"
          name={item.id}
          onChange={this.handleAdhocChange}
          type={isFreeItem ? 'text' : 'number'}
          step={isFreeItem ? null : '.01'}
          label={`${item.name} (${item.formulaTitle})`}
          ref={this.validation[item.id]}
          validation={validations}
          value={item.formulaValue}
          validateLabel={item.name}
        />
        <Button
          tiny
          color="red"
          icon="trash alternate"
          disabled={this.props.loading}
          loading={this.props.loading}
          style={{ marginLeft: '1em', marginBottom: '1px' }}
          onClick={() => this.handleRemoveAdhoc(item.id)}
        />
      </section>
    );
  })

  render() {
    const { globalSettings, bookingData, loading, t } = this.props;
    const { adhocFormulas } = this.state;
    const listCalcs = this.getCalculations('list_price');
    const spaCalcs = this.getCalculations('spa_price');
    const listAdhocs = this.getAdhocCalculations('list_price');
    const spaAdhocs = this.getAdhocCalculations('spa_price');
    // CPI-977 requirements 7/11: belt and braces - the modal is also reachable by a deep link.
    const statusID = bookingData && bookingData.statusID;
    if (isPricingPackageLocked(this.props.auth, statusID, 'pricing')) {
      return (
        <section className="edit_package_pricing">
          <p className="text-body">
            <Icon name="lock" />
            {this.props.t(vnPackageLockMessage(this.props.auth, statusID, 'pricing'))}
          </p>
        </section>
      );
    }
    return (
      <section className="edit_package_pricing">
        <Grid columns="equal" stackable>
          <Grid.Row>
            <Grid.Column>
              <p className="text-header">Formula(s)</p>
              {listCalcs.length > 0 && this.renderFieldHeader(globalSettings && globalSettings.unitSellingPrice ? globalSettings.unitSellingPrice : t('Unit Selling Price'))}
              {listCalcs.length > 0 && this.renderFields(listCalcs)}
              {spaCalcs.length > 0 && this.renderFieldHeader(globalSettings && globalSettings.sellingPrice ? globalSettings.sellingPrice : t('Selling Price'))}
              {spaCalcs.length > 0 && this.renderFields(spaCalcs)}
            </Grid.Column>
            {
              (adhocFormulas.length > 0 || listAdhocs.length > 0 || spaAdhocs.length > 0) ? (
                <Grid.Column>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '-0.6em',
                      marginBottom: '1em',
                    }}
                  >
                    <p className="text-header" style={{ margin: 0 }}>
                      Adhoc Formula(s)
                    </p>
                    {
                      adhocFormulas.length > 0 && (
                        <Dropdown
                          icon="add"
                          floating
                          button
                          color="blue"
                          className="icon"
                          selectOnBlur={false}
                          noResultsMessage={t('No results found', { ns: TRANSLATION_FILES.translation })}
                          style={{
                            marginLeft: '0.5em',
                            padding: '0.5em',
                          }}
                          disabled={this.state.loading}
                        >
                          <Dropdown.Menu style={{ zIndex: 60 }}>
                            {
                              adhocFormulas.map(af => (
                                <Dropdown.Item
                                  key={af.id}
                                  value={af.id}
                                  onClick={this.handleAddAdhoc}
                                  text={af.name}
                                />
                              ))
                            }
                          </Dropdown.Menu>
                        </Dropdown>
                      )
                    }
                  </div>
                  {listAdhocs.length > 0 && this.renderFieldHeader(globalSettings && globalSettings.unitSellingPrice ? globalSettings.unitSellingPrice : t('Unit Selling Price'))}
                  {listAdhocs.length > 0 && this.renderAdhocFields(listAdhocs)}
                  {spaAdhocs.length > 0 && this.renderFieldHeader(globalSettings && globalSettings.sellingPrice ? globalSettings.sellingPrice : t('Selling Price'))}
                  {spaAdhocs.length > 0 && this.renderAdhocFields(spaAdhocs)}
                </Grid.Column>
              ) : null
            }
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <p>
                Would you like to add more formula? Go to&nbsp;
                <a className="link" href={this.getDevPPUrl()}>Pricing Package</a>&nbsp;
                to add, remove or edit formula(s).
              </p>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid columns={1}>
          <Grid.Row>
            <Grid.Column textAlign="right">
              <Button
                content="Save"
                disabled={!bookingData || !bookingData.packagePricingID}
                loading={loading}
                className="submit-button text-title"
                onClick={this.handleSubmit}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </section>
    );
  }
}

export default withTranslation(TRANSLATION_FILES.screens)(CustomiseFormulaModal);



// WEBPACK FOOTER //
// ./src/components/CustomiseFormulaModal.jsx